import { isVisibleComponetBaseOnRole } from "@/commons/RoleFunction.ts";
import { IOption } from "@/dependencies";
import {
  Dropdown,
  Field,
  FieldProps,
  Listbox,
  makeStyles,
  mergeClasses,
  Option,
  OptionOnSelectData,
  SelectionEvents,
  Slot,
} from "@fluentui/react-components";
import { useEffect, useState } from "react";
import { FluentProvider, Text } from "../../";

interface DropdownFieldProps {
  rcName?: string;
  theme?: string;
  name?: string;
  rcRole?: string[];
  requireRoles?: string[];
  selectedOptions?: string[];
  requireOwner?: boolean;
  isOwner?: boolean;
  defaultSelectedOptions?: string[];
  multiselect?: boolean;
  disabled?: boolean;
  options?: IOption[];
  listbox?: Slot<typeof Listbox>;
  onSelectOption?: (event: SelectionEvents, data: OptionOnSelectData) => void;
  placeholder?: string;
  appearance?: "filled-darker" | "filled-lighter" | "outline" | "underline";
  class?: number;
  fallbackText?: string;
}

const useStyles = makeStyles({
  root: {
    fontWeight: 600,
    "& .text-ellipsis": {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  dropdown: {
    maxHeight: "300px",
  },
  displayValue: {
    "& button": {
      color: "var(--colorNeutralForeground1)",
    },
  },
  disabled: {
    backgroundColor: "var(--colorSubtleBackgroundHover)",
    "& button": {
      color: "var(--colorNeutralForeground1)",
    },
  },
  textEllipsis: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
});

const DropdownField = (props: DropdownFieldProps & FieldProps) => {
  const styles = useStyles();
  const [options, setOptions] = useState<IOption[]>([]);
  const [mapOptions, setMapOptions] = useState<Record<string, IOption>>({});
  const isCanBeVisible =
    isVisibleComponetBaseOnRole(props.rcRole, props.requireRoles) &&
    (props.requireOwner ? props.isOwner : true);

  useEffect(() => {
    setOptions([...(props.options || [])]);
    const mapTemp: Record<string, IOption> = {};
    props.options?.forEach((item) => {
      mapTemp[item.value] = item;
    });
    setMapOptions(mapTemp);
  }, [props.options]);

  const onOptionSelect = (event: SelectionEvents, data: OptionOnSelectData) => {
    if (props.onSelectOption) {
      props.onSelectOption(event, data);
    }
  };
  return props.theme ? (
    <FluentProvider theme={props.theme}>
      <Field
        label={props.label}
        validationMessage={props.validationMessage}
        validationMessageIcon={props.validationMessageIcon}
        validationState={props.validationState}
        required={props.required}
        className={mergeClasses(styles.root, props.className)}
      >
        <Dropdown
          appearance={props.appearance}
          {...(props.selectedOptions
            ? { selectedOptions: props.selectedOptions }
            : { defaultSelectedOptions: props.defaultSelectedOptions })}
          onOptionSelect={onOptionSelect}
          multiselect={props.multiselect}
          disabled={props.disabled || !isCanBeVisible}
          listbox={
            props.listbox ? (
              props.listbox
            ) : (
              <div
                className="listbox"
                style={{ maxHeight: 300, overflowY: "auto" }}
              >
                {(options || []).map((item, index) => (
                  <Option key={`${props.rcName}-${index}`} value={item.value} text={item.text || ""}>
                  </Option>
                ))}
              </div>
            )
          }
          placeholder={props.placeholder}
          button={
            props.selectedOptions?.length ? (
              <Text className="text-ellipsis">
                {props.selectedOptions
                  .map((item) => mapOptions[item]?.text || props.fallbackText || "")
                  .join(", ")}
              </Text>
            ) : undefined
          }
          className={mergeClasses(
            styles.dropdown,
            props.selectedOptions?.length ? styles.displayValue : "",
            props.disabled ? styles.disabled : "",
            props?.className
          )}
        >
          {(options || []).map((item, index) => (
            <Option
              key={`${props.rcName}-${index}`}
              value={item.value}
              text={item.text}
            >
              <Text className={styles.textEllipsis}>{item.text}</Text>
            </Option>
          ))}
        </Dropdown>
      </Field>
    </FluentProvider>
  ) : (
    <Field
      label={props.label}
      validationMessage={props.validationMessage}
      validationMessageIcon={props.validationMessageIcon}
      validationState={props.validationState}
      required={props.required}
      className={mergeClasses(styles.root, props.className)}
    >
      <Dropdown
        appearance={props.appearance}
        {...(props.selectedOptions
          ? { selectedOptions: props.selectedOptions }
          : { defaultSelectedOptions: props.defaultSelectedOptions })}
        onOptionSelect={onOptionSelect}
        multiselect={props.multiselect}
        disabled={props.disabled || !isCanBeVisible}
        listbox={
          props.listbox ? (
            props.listbox
          ) : (
            <div
              className="listbox"
              style={{ maxHeight: 300, overflowY: "auto" }}
            >
              {(options || []).map((item, index) => (
                <Option
                  key={`${props.rcName}-${index}`}
                  value={item.value}
                  text={item.text}
                >
                  <Text className={styles.textEllipsis}>{item.text}</Text>
                </Option>
              ))}
            </div>
          )
        }
        placeholder={props.placeholder}
        button={
          props.selectedOptions?.length ? (
            <Text className="text-ellipsis">
              {props.selectedOptions
                .map((item) => mapOptions[item]?.text || props.fallbackText || "")
                .join(", ")}
            </Text>
          ) : undefined
        }
        className={mergeClasses(
          styles.dropdown,
          props.selectedOptions?.length ? styles.displayValue : "",
          props.disabled ? styles.disabled : "",
          props?.className
        )}
      ></Dropdown>
    </Field>
  );
};

export default DropdownField;
